import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Map, TileLayer, WMSTileLayer, GeoJSON } from 'react-leaflet'
import L from 'leaflet'
import { Provider } from 'mobx-react'
import { uiStore, dataStore, selectionStore, rightSidebarStore } from '../stores/UIStore'
import ErrorStore from '../stores/ErrorStore'
import api from '../api'
import constants from '../constants'
import densityLayers from '../densityLayers'
import GoogleLayer from './map/GoogleLayer'
import { BASE_MAP_OPTIONS } from './common/ShareImageMapSettings'
import { MAP_SETTINGS_IMAGES } from '../assets'
import Icons from './common/Icons'
import LayersIcon from './common/icons/LayersIcon'
import CloseIcon from './common/icons/CloseIcon'
import useClickOutside from './common/useClickOutside'
import { geoJSONBounds } from '../utils'
import Wkt from 'wicket/wicket'
import AuthLoader from './common/AuthLoader'

const normalizeGeoJSON = (geo) => {
  if (!geo) return null
  let g = geo
  if (typeof g === 'string') {
    try {
      g = JSON.parse(g)
    } catch (e) {
      try {
        g = new Wkt.Wkt().read(g).toJson()
      } catch (err) {
        return null
      }
    }
  }
  if (!g || typeof g !== 'object' || Array.isArray(g) || !g.type) {
    return null
  }
  return g
}

const roundAndFormat = (num) => {
  if (num == null || isNaN(Number(num))) return '0'
  return Math.round(Number(num)).toLocaleString()
}

const renderPointMarker = (color, name) => (feature, latlng) => {
  const label = name || (feature && feature.properties && (feature.properties.name || feature.properties.title)) || ''
  const labelHtml = label ? `<span class="marker-label leaflet-interactive">${label}</span>` : ''
  const innerColor = (color && color !== '#0a2c4f' && color !== '#102a43' && color !== '#162a3f' && color !== '#18262B' && color !== 'rgba(10, 44, 79, 0.6)') ? color : '#FFFFFF'

  if (typeof L !== 'undefined' && L.divIcon) {
    return L.marker(latlng, {
      icon: L.divIcon({
        className: 'custom-map-marker',
        html: `
          <svg class="map-marker__drop" viewBox="0 0 225.93 225.93" style="color: #102a43; fill: currentColor;">
            <path d="M158.657,43.34c-21.05-24.471-41.786-40.395-42.658-41.061L113.011,0l-3.021,2.234 c-0.873,0.646-21.634,16.104-42.701,40.347c-28.617,32.931-43.127,64.741-43.127,94.547c0,48.966,39.836,88.802,88.802,88.802 s88.803-39.836,88.803-88.802C201.767,108.149,187.262,76.594,158.657,43.34z" />
          </svg>
          <div class="map-marker__icon" style="background-color: ${innerColor}; border-radius: 50%;"></div>
          ${labelHtml}
        `
      })
    })
  }
  return L.circleMarker(latlng, {
    radius: 7,
    fillColor: innerColor,
    color: '#102a43',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.95
  })
}

const isIsochroneItem = (elem, geo) => {
  if (!elem && !geo) return false
  const typeStr = String(elem?.type || elem?.shape?.type || elem?.layer || '').toLowerCase()
  if (
    typeStr === 'isochrone' ||
    typeStr === 'isochrone_shape' ||
    typeStr === 'bulk_isochrone'
  ) {
    return true
  }
  if (elem?.travelTime != null || elem?.buffer?.travelTime != null) {
    return true
  }
  if (elem?.modeOfTransportation != null || elem?.buffer?.modeOfTransportation != null) {
    return true
  }
  if (typeof elem?.name === 'string' && /isochrone/i.test(elem.name)) {
    return true
  }
  const geoProps = geo?.properties || (geo?.features && geo.features[0]?.properties) || {}
  if (geoProps.isochrone || geoProps.contour != null || geoProps.metric != null) {
    return true
  }
  if (typeof geoProps.name === 'string' && /isochrone/i.test(geoProps.name)) {
    return true
  }
  return false
}

// Memoized Map Canvas to completely prevent re-renders / flickering when UI controls or buttons are clicked
const SharedMapCanvas = React.memo(({
  mapRef,
  defaultCenter,
  defaultZoom,
  selectedBaseMap,
  isHeatmapActive,
  densityUrl,
  selectedDensityLayer,
  densityYear,
  geometries
}) => {
  const activeBase = BASE_MAP_OPTIONS.find(b => b.id === selectedBaseMap) || BASE_MAP_OPTIONS[3]

  const renderBaseLayer = () => {
    if (activeBase.id === 'openStreet') {
      return (
        <TileLayer
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
      )
    }
    return (
      <GoogleLayer
        key={activeBase.id}
        noWrap={false}
        bounds={[[-90, -180], [90, 180]]}
        apiKey={constants.GOOGLE_KEY}
        type={activeBase.type}
      />
    )
  }

  return (
    <div className='shared-image-map-container'>
      <Map
        ref={mapRef}
        center={defaultCenter}
        zoom={defaultZoom}
        zoomControl={false}
        attributionControl
        className='shared-image-leaflet-map'
      >
        {renderBaseLayer()}

        {/* Heatmap / Population Density WMS Layer */}
        {isHeatmapActive && densityUrl && (
          <WMSTileLayer
            noWrap
            bounds={[[-90, -180], [90, 180]]}
            key={`density-${selectedDensityLayer}-${densityYear}`}
            url={densityUrl}
            layers={selectedDensityLayer}
            version={densityLayers.version}
            format={densityLayers.format}
            opacity={0.75}
            zIndex={2}
            transparent
            tiled
            tilesOrigin={[-90, -180]}
          />
        )}

        {/* Render Shape Geometries */}
        {(geometries || []).map((g, idx) => {
          if (!g || !g.geoJSON) return null
          const isIso = isIsochroneItem(g, g.geoJSON)
          const hasBorder = Boolean(g.hasBorder || isIso || g.borderColor || g.borderWidth != null || g.borderDashArray)
          const strokeColor = g.borderColor || (hasBorder ? (g.borderColor || '#ff5500') : (g.color || '#0E7490'))
          const strokeWidth = Number(g.borderWidth || (hasBorder ? 3 : 3))
          const dashArray = g.borderDashArray || (isIso ? '7' : null)
          const strokeOpacity = g.borderOpacity != null ? g.borderOpacity : 0.95

          return (
            <GeoJSON
              key={`shared_${g.id || idx}_${g.color}_${g.borderColor || ''}_${g.borderWidth || ''}_${g.borderDashArray || ''}`}
              data={g.geoJSON}
              pointToLayer={renderPointMarker(g.color, g.name || g.label)}
              style={(feature) => {
                const featProps = feature?.properties || {}
                const featBorderColor = featProps.borderColor || featProps.border_color || featProps.stroke || featProps.strokeColor
                const featBorderWidth = featProps.borderWidth || featProps.border_width || featProps['stroke-width']
                const featDashArray = featProps.borderDashArray || featProps.dashArray || featProps['stroke-dasharray']

                return {
                  color: featBorderColor || strokeColor,
                  fillColor: g.color || '#0E7490',
                  fillOpacity: g.transparency != null ? g.transparency : 0.45,
                  weight: Number(featBorderWidth || strokeWidth),
                  opacity: strokeOpacity,
                  dashArray: (featDashArray || dashArray) || undefined
                }
              }}
            />
          )
        })}
      </Map>
    </div>
  )
})

const SharedImageView = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sharedData, setSharedData] = useState(null)
  const [selectedBaseMap, setSelectedBaseMap] = useState('googleHybrid')
  const [selectedDensityLayer, setSelectedDensityLayer] = useState('population_both_cohorts:landscan_pop_2016')
  const [densityYear, setDensityYear] = useState(2016)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)
  const [showHeatmapMenu, setShowHeatmapMenu] = useState(false)
  const [showBaseMapMenu, setShowBaseMapMenu] = useState(false)
  const [copiedEmbed, setCopiedEmbed] = useState(false)
  const mapRef = useRef(null)
  const baseMapMenuRef = useRef(null)
  const heatmapMenuRef = useRef(null)

  const isEmbedded = React.useMemo(() => {
    try {
      if (window.self !== window.top) return true
    } catch (e) {
      return true
    }
    if (typeof window !== 'undefined' && window.location) {
      const search = window.location.search || ''
      const params = new URLSearchParams(search)
      return (
        params.get('embed') === 'true' ||
        params.get('embedded') === 'true' ||
        params.get('is_embed') === 'true' ||
        params.has('embed') ||
        params.has('embedded')
      )
    }
    return false
  }, [])

  const handleCopyEmbed = async () => {
    const embedUrl = `${window.location.origin}/shared/${id}?embed=true`
    const embedCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" style="border:0;" allowfullscreen></iframe>`
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(embedCode)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = embedCode
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        textArea.remove()
      }
      setCopiedEmbed(true)
      setTimeout(() => setCopiedEmbed(false), 2500)
    } catch (err) {
      console.error('Failed to copy iframe embed code:', err)
    }
  }

  useClickOutside(baseMapMenuRef, () => setShowBaseMapMenu(false), showBaseMapMenu)
  useClickOutside(heatmapMenuRef, () => setShowHeatmapMenu(false), showHeatmapMenu)

  useEffect(() => {
    let isMounted = true
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await api.getSharedImage(id)
        if (isMounted) {
          setSharedData(res.data)
          if (res.data && res.data.map_state) {
            if (res.data.map_state.density_layer) {
              setSelectedDensityLayer(res.data.map_state.density_layer)
            }
            if (res.data.map_state.current_year) {
              setDensityYear(res.data.map_state.current_year)
            }
            if (res.data.map_state.base_map) {
              setSelectedBaseMap(res.data.map_state.base_map)
            }
          }
        }
      } catch (err) {
        console.error('Error fetching shared image data:', err)
        if (isMounted) {
          setError(
            (err.response && err.response.data && err.response.data.detail) ||
            'Unable to load the shared location. The link may be invalid or expired.'
          )
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    if (id) {
      fetchData()
    }
    return () => {
      isMounted = false
    }
  }, [id])

  const normalizedGeometries = React.useMemo(() => {
    const rawList = sharedData?.geometries || []
    return rawList
      .map((g) => {
        if (!g) return null
        const geo = normalizeGeoJSON(g.geoJSON)
        if (!geo) return null
        return {
          ...g,
          geoJSON: geo
        }
      })
      .filter(Boolean)
  }, [sharedData?.geometries])

  // Fit map bounds to loaded shapes
  useEffect(() => {
    if (!sharedData || !mapRef.current) return

    let calculatedBounds = null

    for (const g of normalizedGeometries) {
      if (g && g.geoJSON) {
        try {
          const b = geoJSONBounds(g.geoJSON)
          if (b && b.length === 2 && !isNaN(b[0][0]) && !isNaN(b[1][0])) {
            if (!calculatedBounds) {
              calculatedBounds = [[b[0][0], b[0][1]], [b[1][0], b[1][1]]]
            } else {
              calculatedBounds[0][0] = Math.min(calculatedBounds[0][0], b[0][0])
              calculatedBounds[0][1] = Math.min(calculatedBounds[0][1], b[0][1])
              calculatedBounds[1][0] = Math.max(calculatedBounds[1][0], b[1][0])
              calculatedBounds[1][1] = Math.max(calculatedBounds[1][1], b[1][1])
            }
          }
        } catch (e) {
          console.warn('Error calculating geometry bounds:', e)
        }
      }
    }

    const leafletMap = mapRef.current.leafletElement
    if (leafletMap) {
      setTimeout(() => {
        leafletMap.invalidateSize()
        if (calculatedBounds) {
          leafletMap.fitBounds(calculatedBounds, {
            padding: [40, 40],
            maxZoom: 16,
            animate: true
          })
        } else if (sharedData.map_state && sharedData.map_state.center) {
          leafletMap.setView(
            sharedData.map_state.center,
            sharedData.map_state.zoom || 13,
            { animate: true }
          )
        }
      }, 150)
    }
  }, [sharedData, normalizedGeometries])

  // Dynamically attach responsive viewport meta tag ONLY for SharedImageScreen
  useEffect(() => {
    let metaTag = document.querySelector('meta[name="viewport"]')
    const existed = Boolean(metaTag)
    const originalContent = metaTag ? metaTag.getAttribute('content') : null

    if (!metaTag) {
      metaTag = document.createElement('meta')
      metaTag.name = 'viewport'
      document.head.appendChild(metaTag)
    }
    metaTag.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover')

    return () => {
      if (existed && originalContent) {
        metaTag.setAttribute('content', originalContent)
      } else if (!existed && metaTag && metaTag.parentNode) {
        metaTag.parentNode.removeChild(metaTag)
      }
    }
  }, [])

  // Handle window resizing and orientation changes for full-bleed map
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current && mapRef.current.leafletElement) {
        mapRef.current.leafletElement.invalidateSize()
      }
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  // WMS Density URL calculation
  const getDensityUrl = (layerId, year) => {
    if (!layerId || layerId === 'none') return ''
    const layer = (densityLayers.layers || []).find(l => l.id === layerId)
    if (!layer || !layer.workspace) return ''
    const workspace = layer.workspace
    const yr = year || 2016
    return yr === 2016
      ? `${densityLayers.server}wms/${workspace}`
      : `${densityLayers.server}wms/${workspace}_${yr}`
  }

  const densityUrl = getDensityUrl(selectedDensityLayer, densityYear)
  const isHeatmapActive = selectedDensityLayer !== 'none'

  const handleToggleHeatmap = () => {
    if (isHeatmapActive) {
      setSelectedDensityLayer('none')
      setShowHeatmapMenu(false)
    } else {
      setSelectedDensityLayer('population_both_cohorts:landscan_pop_2016')
    }
  }

  const defaultCenter = (sharedData && sharedData.map_state && sharedData.map_state.center) || [37.7749, -122.4194]
  const defaultZoom = (sharedData && sharedData.map_state && sharedData.map_state.zoom) || 12

  const incomeVal = sharedData ? sharedData.household_income : 0
  const daytimePop = sharedData ? sharedData.daytime_population : 0
  const residentialPop = sharedData ? sharedData.residential_population : 0
  const targetName = (sharedData && sharedData.target_name) || 'Selected Territory'
  const senderName = (sharedData && sharedData.sender_name) || 'Population Explorer User'
  const greetingMsg = (sharedData && sharedData.message) || ''
  const description = (sharedData && sharedData.description) || ''

  const activeDensityLayerObj = (densityLayers.layers || []).find(l => l.id === selectedDensityLayer)
  const activeBase = BASE_MAP_OPTIONS.find(b => b.id === selectedBaseMap) || BASE_MAP_OPTIONS[3]

  if (loading) {
    return (
      <AuthLoader
        title='Population Explorer'
        message='Loading shared view...'
      />
    )
  }

  return (
    <div className='shared-image-screen'>
      {/* Navbar Popex */}
      <header className='shared-image-header'>
        <div className='shared-image-header__center'>
          <a
            href='/'
            className='shared-image-header__logo-link'
            target={isEmbedded ? '_blank' : '_self'}
            rel={isEmbedded ? 'noopener noreferrer' : undefined}
          >
            <img
              src='https://framerusercontent.com/images/A6HjTscCJzqfAFEQ903kGfQWk.png'
              alt='Population Explorer'
              className='shared-image-header__logo-img'
            />
          </a>
        </div>
      </header>

      {/* Main Content Area */}
      <div className='shared-image-body'>
        {error && (() => {
          const isExpired = typeof error === 'string' && error.toLowerCase().includes('expired')
          return (
            <div className='shared-image-error'>
              {isExpired
                ? (
                  <div className='shared-image-error__card shared-image-error__card--expired'>
                    <div className='shared-image-error__icon shared-image-error__icon--expired'>
                      <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                        <circle cx='12' cy='12' r='10' />
                        <polyline points='12 6 12 12 16 14' />
                      </svg>
                    </div>
                    <div className='shared-image-error__tag'>Link Expired</div>
                    <h2 className='shared-image-error__title'>Shared Map Link Has Expired</h2>
                    <p className='shared-image-error__message'>{error}</p>

                    <div className='shared-image-error__info-box'>
                      <div className='shared-image-error__info-icon'>
                        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                          <circle cx='12' cy='12' r='10' />
                          <line x1='12' y1='16' x2='12' y2='12' />
                          <line x1='12' y1='8' x2='12.01' y2='8' />
                        </svg>
                      </div>
                      <div className='shared-image-error__info-text'>
                        Shared map links are temporary and expire automatically. If you need access to this location analysis, please ask the sender to generate a fresh link.
                      </div>
                    </div>

                    <div className='shared-image-error__actions'>
                      <a href='/' className='shared-image-error__btn shared-image-error__btn--primary'>
                        Open Population Explorer
                      </a>
                      <a
                        href='https://populationexplorer.com'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='shared-image-error__btn shared-image-error__btn--secondary'
                      >
                        Learn More
                      </a>
                    </div>
                  </div>
                  )
                : (
                  <div className='shared-image-error__card'>
                    <div className='shared-image-error__icon'>!</div>
                    <h2 className='shared-image-error__title'>Location Not Available</h2>
                    <p className='shared-image-error__message'>{error}</p>
                    <div className='shared-image-error__actions'>
                      <a href='/' className='shared-image-error__btn shared-image-error__btn--primary'>
                        Go to Homepage
                      </a>
                    </div>
                  </div>
                  )}
            </div>
          )
        })()}

        {!error && sharedData && (
          <>
            {/* Interactive Leaflet Map (Memoized to prevent flickering) */}
            <SharedMapCanvas
              mapRef={mapRef}
              defaultCenter={defaultCenter}
              defaultZoom={defaultZoom}
              selectedBaseMap={selectedBaseMap}
              isHeatmapActive={isHeatmapActive}
              densityUrl={densityUrl}
              selectedDensityLayer={selectedDensityLayer}
              densityYear={densityYear}
              geometries={normalizedGeometries}
            />

            {/* Map Floating Control Buttons (Zoom) */}
            <div className='shared-image-map-controls'>
              <button
                type='button'
                className='shared-image-map-btn'
                onClick={() => {
                  const m = mapRef.current && mapRef.current.leafletElement
                  if (m) m.zoomIn()
                }}
                title='Zoom in'
              >
                +
              </button>
              <button
                type='button'
                className='shared-image-map-btn'
                onClick={() => {
                  const m = mapRef.current && mapRef.current.leafletElement
                  if (m) m.zoomOut()
                }}
                title='Zoom out'
              >
                −
              </button>
            </div>

            {/* Map Bottom-Left Controls (Heat Map & Base Map Layers - Matching App Screenshots) */}
            <div className='shared-image-map-bottom-controls'>
              {/* Heatmap Button & Dropdown */}
              <div ref={heatmapMenuRef} className='map-controls__heatmap'>
                <button
                  type='button'
                  className={`map-controls__heat-btn ${isHeatmapActive ? 'active' : ''}`}
                  onClick={handleToggleHeatmap}
                  title={isHeatmapActive ? 'Disable heat map' : 'Enable heat map'}
                >
                  <img
                    className='map-controls__heat-img'
                    src={MAP_SETTINGS_IMAGES.HEAT_MAP}
                    alt='Heatmap graphic'
                  />
                  <span className='map-controls__heat-text'>Heat Map</span>
                </button>
                <button
                  type='button'
                  className='map-controls__show-options'
                  onClick={() => setShowHeatmapMenu(!showHeatmapMenu)}
                  title='Heatmap options'
                >
                  <span className='map-controls__dots' />
                </button>

                {/* Heatmap Demographic Layer Options Menu */}
                {showHeatmapMenu && (
                <div className='heatmap-options'>
                  <ul className='heatmap-options__list'>
                    <li className='heatmap-options__item'>
                      <input
                        onChange={(e) => {
                          setSelectedDensityLayer(e.target.value)
                          setShowHeatmapMenu(false)
                        }}
                        value='none'
                        className='heatmap-options__input'
                        id='shared-none'
                        type='radio'
                        name='density'
                        checked={selectedDensityLayer === 'none'}
                      />
                      <label
                        className={`heatmap-options__label ${selectedDensityLayer === 'none' ? 'active' : ''}`}
                        htmlFor='shared-none'
                      >
                        None
                      </label>
                    </li>
                    {(densityLayers.layers || []).map((layer) => {
                      const { id: value, name: label } = layer
                      return (
                        <li className='heatmap-options__item' key={value}>
                          <input
                            onChange={(e) => {
                              setSelectedDensityLayer(e.target.value)
                              setShowHeatmapMenu(false)
                            }}
                            value={value}
                            className='heatmap-options__input'
                            id={`shared-${value}`}
                            type='radio'
                            name='density'
                            checked={selectedDensityLayer === value}
                          />
                          <label
                            className={`heatmap-options__label ${selectedDensityLayer === value ? 'active' : ''}`}
                            htmlFor={`shared-${value}`}
                          >
                            {label}
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                </div>
                )}
              </div>

              {/* Base Map Layers Button & Sleek Popup Menu */}
              <div ref={baseMapMenuRef} className='layer-settings'>
                <button
                  className='layer-settings__btn'
                  type='button'
                  onClick={() => setShowBaseMapMenu(!showBaseMapMenu)}
                  title='Map layers'
                >
                  <img
                    className='layer-settings__img'
                    src={activeBase.img || MAP_SETTINGS_IMAGES.GOOGLE_HYBRID_LAYER}
                    alt='Layer image'
                  />
                  <span className='layer-settings__info'>
                    <LayersIcon />
                    <span className='layer-settings__name'>Layers</span>
                  </span>
                </button>

                {/* Sleek Base Map Popup Menu */}
                {showBaseMapMenu && (
                <div className='map-settings-sleek'>
                  <div className='map-settings-sleek__header'>
                    <h2 className='map-settings-sleek__title'>Base map</h2>
                    <button
                      style={{ width: 'fit-content' }}
                      onClick={() => setShowBaseMapMenu(false)}
                      type='button'
                      className='map-settings-sleek__close-btn'
                      title='Close'
                    >
                      <CloseIcon />
                    </button>
                  </div>

                  <div className='map-settings-sleek__list'>
                    {BASE_MAP_OPTIONS.map((layer) => {
                      const isSelected = selectedBaseMap === layer.id
                      return (
                        <div
                          key={layer.id}
                          onClick={() => {
                            setSelectedBaseMap(layer.id)
                            setShowBaseMapMenu(false)
                          }}
                          className={`map-settings-sleek__item ${isSelected ? 'map-settings-sleek__item--selected' : ''}`}
                        >
                          <div className='map-settings-sleek__thumb'>
                            <img
                              src={layer.img}
                              alt={layer.fallbackLabel}
                            />
                          </div>
                          <span className='map-settings-sleek__name'>
                            {layer.fallbackLabel}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                )}
              </div>
            </div>

              {/* Heatmap Legend */}
              {isHeatmapActive && (
                <div className='shared-image-heatmap-legend'>
                  <div className='shared-image-heatmap-legend__header'>
                    <span className='shared-image-heatmap-legend__icon'>🔥</span>
                    <span className='shared-image-heatmap-legend__text'>
                      {activeDensityLayerObj ? activeDensityLayerObj.name : 'Population Density'}
                    </span>
                  </div>
                  <div className='shared-image-heatmap-legend__gradient' />
                  <div className='shared-image-heatmap-legend__labels'>
                    <span>Low Density</span>
                    <span>High Density</span>
                  </div>
                </div>
              )}

            {/* Floating Territory Highlights Panel */}
            <div className={`shared-image-panel ${isPanelCollapsed ? 'shared-image-panel--collapsed' : ''}`}>
              <div
                className='shared-image-panel__header'
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setIsPanelCollapsed(!isPanelCollapsed)
                  }
                }}
              >
                <div className='shared-image-panel__header-row'>
                  <h1 className='shared-image-panel__name' title={targetName}>
                    {targetName}
                  </h1>
                  <button
                    type='button'
                    className='shared-image-panel__toggle-btn'
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsPanelCollapsed(!isPanelCollapsed)
                    }}
                    title={isPanelCollapsed ? 'Expand details' : 'Collapse details'}
                    aria-expanded={!isPanelCollapsed}
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{
                        transform: isPanelCollapsed ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <polyline points='6 9 12 15 18 9' />
                    </svg>
                  </button>
                </div>
              </div>

              {!isPanelCollapsed && (
                <div className='shared-image-panel__body'>
                  {/* Stats Cards */}
                  <div className='shared-image-stats-grid'>
                    <div className='shared-image-stat-card shared-image-stat-card--primary'>
                      <div className='shared-image-stat-card__val'>
                        {roundAndFormat(daytimePop)}
                      </div>
                      <div className='shared-image-stat-card__label'>Daytime Population</div>
                    </div>
                    {residentialPop > 0 && (
                    <div className='shared-image-stat-card'>
                      <div className='shared-image-stat-card__val'>
                        {roundAndFormat(residentialPop)}
                      </div>
                      <div className='shared-image-stat-card__label'>Residential Population</div>
                    </div>
                    )}
                    {incomeVal > 0 && (
                      <div className='shared-image-stat-card shared-image-stat-card--primary'>
                        <div className='shared-image-stat-card__val'>
                          ${roundAndFormat(incomeVal)}
                        </div>
                        <div className='shared-image-stat-card__label'>Household Income</div>
                      </div>
                    )}

                  </div>

                  {/* Sender Note / Message */}
                  {/* {greetingMsg && (
                    <div className='shared-image-message-box'>
                      <div className='shared-image-message-box__sender'>
                        From <span className='shared-image-message-box__sender-name'>{senderName}</span>
                      </div>
                      <p className='shared-image-message-box__text'>{greetingMsg}</p>
                    </div>
                  )} */}

                  {/* Description */}
                  {/* {description && (
                    <div className='shared-image-description-box'>
                      <div className='shared-image-description-box__label'>Notes</div>
                      <p className='shared-image-description-box__text'>{description}</p>
                    </div>
                  )} */}

                  {/* Action CTA */}
                  <div className='shared-image-panel__actions'>
                    <a
                      href='/'
                      target={isEmbedded ? '_blank' : '_self'}
                      rel={isEmbedded ? 'noopener noreferrer' : undefined}
                      className='shared-image-panel__primary-action'
                    >
                      View more details &rarr;
                    </a>
                    {!isEmbedded && (
                      <button
                        type='button'
                        className={`shared-image-panel__embed-btn ${copiedEmbed ? 'shared-image-panel__embed-btn--copied' : ''}`}
                        onClick={handleCopyEmbed}
                        title='Copy iframe embed code to clipboard'
                      >
                        <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={copiedEmbed ? '2.5' : '2'} strokeLinecap='round' strokeLinejoin='round'>
                          {copiedEmbed && <polyline points='20 6 9 17 4 12' />}
                          {!copiedEmbed && <polyline points='16 18 22 12 16 6' />}
                          {!copiedEmbed && <polyline points='8 6 2 12 8 18' />}
                        </svg>
                        <span>{copiedEmbed ? 'Copied Embed Code!' : 'Copy embedded link'}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const SharedImageScreen = () => {
  return (
    <Provider
      uiStore={uiStore}
      dataStore={dataStore}
      selectionStore={selectionStore}
      rightSidebarStore={rightSidebarStore}
      errorStore={ErrorStore}
    >
      <Icons />
      <SharedImageView />
    </Provider>
  )
}

export default SharedImageScreen
